'use client'

import { useState, useMemo, useRef, useCallback } from 'react'
import { GAFOR_REGIONS, DWD_VALID_REGION_IDS, type GaforRegion } from '@/data/gafor-regions'

interface RegionForecast {
  climbRate?: number
  pfd?: number
  windSpeed?: number
  windGust?: number
  windDirection?: number
  thermalIndex?: number   // 0-10 Modellflug-Index Thermik
  slopeIndex?: number     // 0-10 Modellflug-Index Hang
}

interface GaforMapProps {
  onSelectRegion: (regionId: number) => void
  activeRegion?: number
  forecasts?: Map<number, RegionForecast>
}

// Germany bounding box for coordinate mapping
const LAT_MIN = 47.0
const LAT_MAX = 55.2
const LON_MIN = 5.6
const LON_MAX = 15.3

const MAP_WIDTH = 420
const MAP_HEIGHT = 520

function latLonToPixel(lat: number, lon: number): { x: number; y: number } {
  const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * MAP_WIDTH
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * MAP_HEIGHT
  return { x, y }
}

function degToCompass(deg: number): string {
  const dirs = ['N','NNO','NO','ONO','O','OSO','SO','SSO','S','SSW','SW','WSW','W','WNW','NW','NNW']
  return dirs[Math.round(deg / 22.5) % 16]
}

function terrainEmoji(type: string): string {
  switch (type) {
    case 'coast': return '\u{1F30A}'
    case 'flatland': return '\u{1F33E}'
    case 'hills': return '\u26F0'
    case 'mountains': return '\u{1F3D4}'
    case 'alps': return '\u{1F3D4}'
    default: return ''
  }
}

// Modellflug-Index berechnen (kombiniert Thermik + Hang)
function getRegionIndex(data?: RegionForecast): number {
  if (!data) return 0
  const ti = data.thermalIndex ?? 0
  const si = data.slopeIndex ?? 0
  return Math.max(ti, si) // Bester Index (Thermik ODER Hang)
}

// Farbe nach Index: rot→orange→gelb→grün (0→10)
function indexToColor(idx: number, alpha: number = 0.3): string {
  if (idx >= 8) return `rgba(34, 197, 94, ${alpha})`     // grün
  if (idx >= 6) return `rgba(132, 204, 22, ${alpha})`    // lime
  if (idx >= 4) return `rgba(234, 179, 8, ${alpha})`     // gelb
  if (idx >= 2) return `rgba(249, 115, 22, ${alpha})`    // orange
  if (idx > 0)  return `rgba(239, 68, 68, ${alpha})`     // rot
  return `rgba(107, 114, 128, ${alpha * 0.5})`            // grau
}

// Border-Farbe (stärker)
function indexToBorder(idx: number): string {
  if (idx >= 8) return 'rgba(34, 197, 94, 0.7)'
  if (idx >= 6) return 'rgba(132, 204, 22, 0.6)'
  if (idx >= 4) return 'rgba(234, 179, 8, 0.5)'
  if (idx >= 2) return 'rgba(249, 115, 22, 0.4)'
  if (idx > 0)  return 'rgba(239, 68, 68, 0.3)'
  return 'rgba(107, 114, 128, 0.15)'
}

// Blink-Geschwindigkeit: hoher Index = schneller pulsierend
function indexToAnimation(idx: number): string {
  if (idx >= 8) return 'gafor-pulse 1.5s ease-in-out infinite'
  if (idx >= 6) return 'gafor-pulse 2s ease-in-out infinite'
  if (idx >= 4) return 'gafor-pulse 3s ease-in-out infinite'
  if (idx >= 2) return 'gafor-pulse 4s ease-in-out infinite'
  return 'none'
}

function regionColor(region: GaforRegion, forecasts?: Map<number, any>): string {
  if (forecasts?.has(region.id)) {
    const idx = getRegionIndex(forecasts.get(region.id))
    return indexToColor(idx, 0.25)
  }
  // Default: terrain-based subtle color
  switch (region.terrainType) {
    case 'coast': return 'rgba(59, 130, 246, 0.12)'
    case 'flatland': return 'rgba(34, 197, 94, 0.08)'
    case 'hills': return 'rgba(234, 179, 8, 0.10)'
    case 'mountains': return 'rgba(249, 115, 22, 0.12)'
    case 'alps': return 'rgba(168, 85, 247, 0.12)'
    default: return 'rgba(107, 114, 128, 0.1)'
  }
}

function regionBorderColor(region: GaforRegion, forecasts?: Map<number, any>): string {
  if (forecasts?.has(region.id)) {
    const data = forecasts.get(region.id)
    const climb = data?.climbRate ?? 0
    if (climb >= 1.5) return 'rgba(34, 197, 94, 0.6)'
    if (climb >= 0.8) return 'rgba(234, 179, 8, 0.6)'
    if (climb > 0) return 'rgba(249, 115, 22, 0.5)'
    return 'rgba(107, 114, 128, 0.3)'
  }
  return 'var(--border)'
}

// Germany border outline (simplified, ~12 key points converted to pixel coords)
const GERMANY_BORDER_PATH = (() => {
  const borderPoints: [number, number][] = [
    [54.91, 8.33],   // Sylt (N)
    [54.79, 9.43],   // Flensburg
    [54.08, 12.13],  // Rostock
    [53.87, 14.17],  // Usedom (NE)
    [52.35, 14.55],  // Frankfurt/Oder
    [51.15, 14.99],  // Goerlitz (E)
    [50.10, 12.10],  // Hof
    [48.57, 13.47],  // Passau (SE)
    [47.63, 13.00],  // Berchtesgaden
    [47.27, 10.18],  // Oberstdorf
    [47.55, 9.68],   // Lindau (S)
    [47.56, 7.59],   // Basel (SW)
    [48.00, 7.80],   // Freiburg
    [49.23, 7.00],   // Saarbruecken
    [49.45, 6.15],   // Trier
    [50.78, 6.08],   // Aachen (W)
    [51.95, 6.75],   // Kleve/Emmerich
    [53.37, 7.21],   // Emden (NW)
    [53.87, 8.70],   // Cuxhaven
    [54.91, 8.33],   // Back to Sylt
  ]
  const pixels = borderPoints.map(([lat, lon]) => latLonToPixel(lat, lon))
  return 'M ' + pixels.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ') + ' Z'
})()

// Tooltip dimensions for overflow calculation
const TOOLTIP_HEIGHT = 220
const TOOLTIP_WIDTH = 200

export function GaforMap({ onSelectRegion, activeRegion, forecasts }: GaforMapProps) {
  const [hoveredRegion, setHoveredRegion] = useState<number | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const calcTooltipPos = useCallback((buttonX: number, buttonY: number) => {
    let x = buttonX + 10
    let y = buttonY + 36

    // Flip up if near bottom
    if (y + TOOLTIP_HEIGHT > MAP_HEIGHT) {
      y = buttonY - TOOLTIP_HEIGHT - 10
    }
    // Flip left if near right edge
    if (x + TOOLTIP_WIDTH > MAP_WIDTH) {
      x = MAP_WIDTH - TOOLTIP_WIDTH - 10
    }
    // Clamp left
    if (x < 4) x = 4
    // Clamp top
    if (y < 4) y = 4

    return { x, y }
  }, [])

  // Only show DWD-valid regions
  const validRegions = useMemo(() =>
    GAFOR_REGIONS.filter(r => DWD_VALID_REGION_IDS.has(r.id)),
    []
  )

  const hoveredData = useMemo(() => {
    if (!hoveredRegion) return null
    return GAFOR_REGIONS.find(r => r.id === hoveredRegion) ?? null
  }, [hoveredRegion])

  return (
    <div className="relative">
      {/* Pulse Animation */}
      <style>{`
        @keyframes gafor-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      {/* Map container */}
      <div
        ref={containerRef}
        className="relative mx-auto overflow-hidden"
        style={{ width: MAP_WIDTH, height: MAP_HEIGHT }}
      >
        {/* Germany outline hint (subtle background) */}
        <div className="absolute inset-0 rounded-[16px] bg-[var(--bg-secondary)] opacity-30" />

        {/* Germany border outline */}
        <svg
          viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 1 }}
        >
          <path
            d={GERMANY_BORDER_PATH}
            fill="var(--bg-secondary)"
            fillOpacity="0.15"
            stroke="var(--border)"
            strokeWidth="1.5"
            opacity="0.4"
            strokeLinejoin="round"
          />
        </svg>

        {/* Region cells */}
        {validRegions.map(region => {
          const { x, y } = latLonToPixel(region.center.lat, region.center.lon)
          const isActive = region.id === activeRegion
          const isHovered = region.id === hoveredRegion
          const hasForecast = forecasts?.has(region.id)

          return (
            <button
              key={region.id}
              className="absolute flex flex-col items-center justify-center cursor-pointer transition-all duration-150 rounded-lg"
              style={{
                left: x - 22,
                top: y - 16,
                width: 44,
                height: 32,
                background: forecasts?.has(region.id)
                  ? indexToColor(getRegionIndex(forecasts.get(region.id)), isHovered ? 0.5 : 0.3)
                  : regionColor(region, forecasts),
                border: isActive
                  ? '2px solid var(--accent)'
                  : isHovered
                  ? '2px solid var(--accent)'
                  : forecasts?.has(region.id)
                  ? `2px solid ${indexToBorder(getRegionIndex(forecasts.get(region.id)))}`
                  : `1px solid ${regionBorderColor(region, forecasts)}`,
                boxShadow: isActive
                  ? '0 0 12px rgba(242,201,78,0.3)'
                  : forecasts?.has(region.id) && getRegionIndex(forecasts.get(region.id)) >= 6
                  ? `0 0 8px ${indexToBorder(getRegionIndex(forecasts.get(region.id)))}`
                  : 'none',
                animation: !isActive && !isHovered && forecasts?.has(region.id)
                  ? indexToAnimation(getRegionIndex(forecasts.get(region.id)))
                  : 'none',
                zIndex: isActive ? 20 : isHovered ? 15 : 10,
                transform: isHovered ? 'scale(1.15)' : 'scale(1)',
              }}
              onClick={() => onSelectRegion(region.id)}
              onMouseEnter={() => {
                setHoveredRegion(region.id)
                const pos = latLonToPixel(region.center.lat, region.center.lon)
                setTooltipPos(calcTooltipPos(pos.x, pos.y))
              }}
              onMouseLeave={() => setHoveredRegion(null)}
              data-tip={`GAFOR ${region.id}: ${region.name}`}
            >
              <span className="text-[8px] font-black text-[var(--accent)] leading-none">
                {region.id}
              </span>
              <span className="text-[7px] font-bold text-[var(--muted)] leading-none truncate max-w-[40px]">
                {region.name.split('/')[0].split(' ')[0]}
              </span>
            </button>
          )
        })}

        {/* Tooltip */}
        {hoveredData && hoveredRegion && (
          <div
            className="absolute z-30 pointer-events-none"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
            }}
          >
            <div className="rounded-xl border border-[var(--accent)]/40 bg-[var(--bg-card)] shadow-lg p-3 min-w-[180px]">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--accent)] text-[9px] font-black text-black">
                  {hoveredData.id}
                </span>
                <span className="text-[11px] font-black text-[var(--text)]">
                  {hoveredData.name}
                </span>
              </div>
              <div className="space-y-1 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-[var(--muted)]">Referenzstation</span>
                  <span className="font-bold text-[var(--text)]">{hoveredData.referenceStation}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--muted)]">Grundhöhe</span>
                  <span className="font-bold text-[var(--text)]">{hoveredData.elevation} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--muted)]">Gelände</span>
                  <span className="font-bold text-[var(--text)]">
                    {terrainEmoji(hoveredData.terrainType)} {hoveredData.terrainType}
                  </span>
                </div>
                {forecasts?.has(hoveredData.id) && (
                  <>
                    <div className="border-t border-[var(--border)] my-1" />
                    {/* Modellflug-Index */}
                    {(forecasts.get(hoveredData.id)?.thermalIndex != null || forecasts.get(hoveredData.id)?.slopeIndex != null) && (
                      <div className="flex justify-between gap-2 my-2 py-2 px-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-[14px]">☀</span>
                          <div>
                            <div className="text-[8px] font-bold text-[var(--muted)] uppercase tracking-wider">Thermik</div>
                            <div className="text-[18px] font-black leading-none" style={{ color: indexToBorder(forecasts.get(hoveredData.id)?.thermalIndex ?? 0), textShadow: `0 0 8px ${indexToBorder(forecasts.get(hoveredData.id)?.thermalIndex ?? 0)}40` }}>
                              {(forecasts.get(hoveredData.id)?.thermalIndex ?? 0).toFixed(1)}
                            </div>
                          </div>
                        </div>
                        <div className="w-px bg-[var(--border)]" />
                        <div className="flex items-center gap-2 flex-1 justify-end">
                          <div className="text-right">
                            <div className="text-[8px] font-bold text-[var(--muted)] uppercase tracking-wider">Hang</div>
                            <div className="text-[18px] font-black leading-none" style={{ color: indexToBorder(forecasts.get(hoveredData.id)?.slopeIndex ?? 0), textShadow: `0 0 8px ${indexToBorder(forecasts.get(hoveredData.id)?.slopeIndex ?? 0)}40` }}>
                              {(forecasts.get(hoveredData.id)?.slopeIndex ?? 0).toFixed(1)}
                            </div>
                          </div>
                          <span className="text-[14px]">⛰</span>
                        </div>
                      </div>
                    )}
                    {forecasts.get(hoveredData.id)?.climbRate != null && (
                      <div className="flex justify-between">
                        <span className="text-[var(--muted)]">Steigen ↑</span>
                        <span className="font-black text-green-400">
                          {forecasts.get(hoveredData.id)!.climbRate!.toFixed(1)} m/s
                        </span>
                      </div>
                    )}
                    {forecasts.get(hoveredData.id)?.pfd != null && (
                      <div className="flex justify-between">
                        <span className="text-[var(--muted)]">MFS (Strecke)</span>
                        <span className="font-black text-[var(--accent)]">
                          {Math.round(forecasts.get(hoveredData.id)!.pfd! / 2)} km
                        </span>
                      </div>
                    )}
                    {forecasts.get(hoveredData.id)?.windSpeed != null && (
                      <div className="flex justify-between">
                        <span className="text-[var(--muted)]">Wind ⌀/Böen</span>
                        <span className="font-black text-[var(--text)]">
                          {forecasts.get(hoveredData.id)!.windSpeed} / {forecasts.get(hoveredData.id)!.windGust || '—'} km/h
                        </span>
                      </div>
                    )}
                    {forecasts.get(hoveredData.id)?.windDirection != null && (
                      <div className="flex justify-between">
                        <span className="text-[var(--muted)]">Richtung</span>
                        <span className="font-black text-[var(--text)] flex items-center gap-1">
                          <svg width="12" height="12" viewBox="0 0 14 14" style={{ transform: `rotate(${(forecasts.get(hoveredData.id)!.windDirection! + 180) % 360}deg)` }}>
                            <path d="M7 2L9 10H5L7 2Z" fill="var(--accent)" />
                          </svg>
                          <span className="text-[var(--accent)]">{degToCompass(forecasts.get(hoveredData.id)!.windDirection!)}</span>
                          <span className="text-[9px] text-[var(--muted)]">{forecasts.get(hoveredData.id)!.windDirection}°</span>
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              {!forecasts?.has(hoveredData.id) && (
                <p className="text-[9px] text-[var(--accent)] font-bold mt-2">Region anklicken</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
        {[
          { label: 'Küste', color: 'rgba(59, 130, 246, 0.5)', emoji: '\u{1F30A}' },
          { label: 'Flachland', color: 'rgba(34, 197, 94, 0.5)', emoji: '\u{1F33E}' },
          { label: 'Mittelgebirge', color: 'rgba(234, 179, 8, 0.5)', emoji: '\u26F0' },
          { label: 'Gebirge', color: 'rgba(249, 115, 22, 0.5)', emoji: '\u{1F3D4}' },
          { label: 'Alpen', color: 'rgba(168, 85, 247, 0.5)', emoji: '\u{1F3D4}' },
        ].map(item => (
          <span key={item.label} className="flex items-center gap-1.5 text-[10px] font-bold text-[var(--muted)]">
            <span className="w-3 h-3 rounded-sm" style={{ background: item.color }} />
            {item.emoji} {item.label}
          </span>
        ))}
        {forecasts && forecasts.size > 0 && (
          <>
            <span className="text-[var(--border)]">|</span>
            <span className="flex items-center gap-1.5 text-[10px] font-bold text-green-400">
              <span className="w-3 h-3 rounded-sm bg-green-500/40" />
              Gute Thermik
            </span>
            <span className="flex items-center gap-1.5 text-[10px] font-bold text-yellow-400">
              <span className="w-3 h-3 rounded-sm bg-yellow-500/40" />
              Moderat
            </span>
          </>
        )}
      </div>

      <p className="text-[9px] text-[var(--muted)] text-center mt-2">
        {validRegions.length} DWD-Gebiete mit Segelflugvorhersage · Quelle: Deutscher Wetterdienst (GeoNutzV)
      </p>
    </div>
  )
}
